import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { keyFromPath } from '../utils/metrics';

// MUI icons
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TimelineIcon from '@mui/icons-material/Timeline';
import VideocamIcon from '@mui/icons-material/Videocam';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import LanguageIcon from '@mui/icons-material/Language';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import NumbersIcon from '@mui/icons-material/Numbers';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import MapIcon from '@mui/icons-material/Map';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TransformIcon from '@mui/icons-material/Transform';

// Grouped by what the investigator is working on. `keywords` are extra words the
// dashboard search matches (alongside title and description); they are not shown.
export const SECTIONS = [
  {
    title: 'License Plate & Vehicles',
    tools: [
      {
        icon: TimelineIcon,
        color: '#15803d',
        bg:   '#dcfce7',
        title: 'LPR Pattern Analyzer',
        description: 'Visualize movement patterns, hourly activity, and day-of-week trends for a vehicle from OpenALPR CSV exports, and predict the next LPR hit.',
        to: '/lprpattern',
        favorite: true,
        keywords: 'plate alpr anpr flock vehicle movement pattern openalpr rekor csv',
      },
      {
        icon: DirectionsCarIcon,
        color: '#0369a1',
        bg:   '#e0f2fe',
        title: 'REKOR / ALPR Tracker',
        description: 'Find plates appearing across multiple LPR sites from REKOR/OpenALPR CSV exports, ranked by location count.',
        to: '/lprtracker',
        keywords: 'plate alpr anpr flock vehicle openalpr rekor csv multiple sites',
      },
      {
        icon: NumbersIcon,
        color: '#b45309',
        bg:   '#fef3c7',
        title: 'VIN Decoder',
        description: 'Validate a VIN’s check digit and decode its country of origin, likely manufacturer, and model year locally. Optional full decode (make, model, engine, plant) via NHTSA’s free public database.',
        to: '/vindecoder',
        tag: 'Beta',
        keywords: 'vehicle identification number car truck nhtsa',
      },
      {
        icon: JoinInnerIcon,
        color: '#9d174d',
        bg:   '#fce7f3',
        title: 'Common Analysis',
        description: 'Find common items between datasets such as LPR data or phone calls.',
        to: '/commonanalysis',
        keywords: 'compare overlap match intersect plates phone numbers datasets',
      },
    ],
  },
  {
    title: 'Legal Process Returns',
    subsections: [
      {
        title: 'Facebook',
        tools: [
          {
            icon: ManageSearchIcon,
            color: '#4f46e5',
            bg:   '#eef2ff',
            title: 'Facebook Warrant Processor',
            description: 'Parse a Facebook search warrant return to browse account info, messages, IP addresses, and friends.',
            to: '/fbscan',
            keywords: 'meta facebook messenger warrant return search warrant',
          },
        ],
      },
      {
        title: 'Instagram',
        tools: [
          {
            icon: PhotoCameraBackIcon,
            color: '#be185d',
            bg:   '#fdf2f8',
            title: 'Instagram Warrant Processor',
            description: 'Parse an Instagram search warrant return to browse account info, photos, videos, followers, messages, and IP addresses.',
            to: '/instagramscan',
            keywords: 'meta instagram dm warrant return search warrant',
          },
        ],
      },
      {
        title: 'Snapchat',
        tools: [
          {
            icon: CameraAltIcon,
            color: '#f59e0b',
            bg:   '#fef3c7',
            title: 'Snapchat Subpoena Processor',
            description: 'Parse a Snapchat law enforcement return ZIP to view account details, IP history, and privacy settings.',
            to: '/snapchat',
            keywords: 'snap snapchat subpoena return',
          },
          {
            icon: CameraAltIcon,
            color: '#eab308',
            bg:   '#fefce8',
            title: 'Snapchat Warrant Processor',
            description: 'Parse Snapchat search warrant return CSV files to view messages, IP history, friends, memories, and more.',
            to: '/snapchatwarrant',
            keywords: 'snap snapchat warrant return messages memories',
          },
        ],
      },
      {
        title: 'Apple',
        tools: [
          {
            icon: InventoryIcon,
            color: '#1d4ed8',
            bg:   '#dbeafe',
            title: 'Apple Legal Process Download',
            description: 'Upload the Account_Data_Links ZIP from Apple, enter the decryption passphrase, and automatically download, decrypt, and organize all files by category.',
            to: '/applelegaldownload',
            favorite: true,
            keywords: 'apple icloud gpg decrypt passphrase account data links download',
          },
          {
            icon: InventoryIcon,
            color: '#0f766e',
            bg:   '#ccfbf1',
            title: 'Apple Legal Process Organizer',
            description: 'Automatically group and organize Apple legal process returns by account for easy download.',
            to: '/applelegal',
            keywords: 'apple icloud organize returns',
          },
          {
            icon: ManageSearchIcon,
            color: '#1e40af',
            bg:   '#dbeafe',
            title: 'Apple Subpoena & 2703(d) Parser',
            description: 'Upload the ZIP from the Apple Legal Process Download tool (and Apple’s production ZIP) to browse subscriber details, iCloud activity, iMessage/FaceTime queries, mail logs, devices, purchases, every IP address and one merged timeline.',
            to: '/apple2703d',
            tag: 'Beta',
            keywords: 'apple icloud subpoena 2703d imessage facetime imei serial dsid apple id subscriber ip',
          },
        ],
      },
    ],
  },
  {
    title: 'Phones & Internet',
    tools: [
      {
        icon: PhoneInTalkIcon,
        color: '#0369a1',
        bg:   '#e0f2fe',
        title: 'Phone Record Processing',
        description: 'Upload AT&T, Verizon, and T-Mobile grand-jury return files — auto-detected, merged across multiple responses, and de-duplicated — for subscriber info, top-caller analysis, and timezone-aware call detail records.',
        to: '/cdranalyzer',
        favorite: true,
        tag: 'Beta',
        keywords: 'cdr call detail records att at&t verizon tmobile t-mobile carrier grand jury subscriber cell',
      },
      {
        icon: ContactPhoneIcon,
        color: '#7c2d12',
        bg:   '#ffedd5',
        title: 'Commercial Phone Number Lookup',
        description: 'Bulk-paste phone numbers to check them against ~15M US business listings (Foursquare, built into this site) — 100% local, nothing is ever sent anywhere. Identifies mapped businesses — not personal callers, which requires legal process to the carrier.',
        to: '/phonelookup',
        tag: '',
        keywords: 'phone number business foursquare caller who owns',
      },
      {
        icon: LanguageIcon,
        color: '#0e7490',
        bg:   '#cffafe',
        title: 'IP and Domain Intelligence',
        description: 'Screen one IP or a whole log against VPN, datacenter, Tor, iCloud Private Relay, and abuse blocklists held in your browser — the addresses you paste are never sent anywhere. Optional live lookup adds registrant, abuse contact, geolocation, hostname, and open ports. Paste a domain or email address instead and it returns who actually handles the mail (who to serve), registration, nameservers, and SPF/DMARC.',
        to: '/iplookup',
        tag: 'Online',
        keywords: 'ip address vpn tor proxy whois domain email mx dns isp datacenter',
      },
      {
        icon: WifiTetheringIcon,
        color: '#0e7490',
        bg:   '#cffafe',
        title: 'Ping Mapper',
        description: 'Paste a cell-ping GPS coordinate and radius from a carrier return and see the search area plotted on a map.',
        to: '/pingmapper',
        tag: 'Online',
        keywords: 'cell phone ping gps location radius carrier map tower',
      },
    ],
  },
  {
    title: 'Requests & Paperwork',
    tools: [
      {
        icon: FolderSpecialIcon,
        color: '#92400e',
        bg:   '#fef3c7',
        title: 'Preservation Tool',
        description: 'Generate preservation requests for phone records or area searches.',
        to: '/preservation',
        favorite: true,
        keywords: 'preserve preservation letter 2703f request phone records',
      },
      {
        icon: DescriptionIcon,
        color: '#4338ca',
        bg:   '#eef2ff',
        title: 'Provider Directory & Rider Generator',
        description: 'Paste an email, phone, URL or handle to see who to serve, how they take legal process and emergency requests, and what each process can reach. Then build the subpoena rider or warrant attachment as a Word document.',
        to: '/subpoenarider',
        keywords: 'subpoena rider attachment warrant word document provider directory isp list search.org legal process exigent emergency request 2703 law enforcement guide who to serve carrier lookup',
      },
    ],
  },
  {
    title: 'Photos & Evidence Files',
    tools: [
      {
        icon: ImageSearchIcon,
        color: '#0f766e',
        bg:   '#ccfbf1',
        title: 'EXIF Viewer',
        description: 'Extract metadata from photos including GPS coordinates, device info, and timestamps. Supports JPEG, HEIC, PNG, and more.',
        to: '/exif',
        keywords: 'photo picture image metadata gps location camera exif heic',
      },
      {
        icon: TransformIcon,
        color: '#7c3aed',
        bg:   '#ede9fe',
        title: 'Image Converter',
        description: 'Convert iPhone HEIC photos to JPG or PNG so they open anywhere. Runs entirely in your browser — nothing is uploaded.',
        to: '/imageconverter',
        tag: 'Beta',
        keywords: 'heic jpg jpeg png photo picture iphone convert',
      },
      {
        icon: FingerprintIcon,
        color: '#065f46',
        bg:   '#d1fae5',
        title: 'File Hash / Chain-of-Custody Verifier',
        description: 'Compute SHA-256, SHA-1, MD5, and SHA-512 hashes of an evidence file, verify against a known value, and export a hash log or PDF report.',
        to: '/hashverifier',
        tag: 'Beta',
        keywords: 'hash sha256 md5 sha1 evidence chain of custody integrity',
      },
    ],
  },
  {
    title: 'Time, Audio & Video',
    tools: [
      {
        icon: VideocamIcon,
        color: '#c2410c',
        bg:   '#ffedd5',
        title: 'DVR Time Converter',
        description: 'Convert incident time to DVR time when the DVR clock is not synced.',
        to: '/dvrtime',
        favorite: true,
        keywords: 'dvr cctv surveillance video clock offset drift time',
      },
      {
        icon: ScheduleIcon,
        color: '#7c3aed',
        bg:   '#f3e8ff',
        title: 'UTC Converter',
        description: 'Quickly convert between UTC and local time.',
        to: '/utcconverter',
        keywords: 'utc gmt timezone time zone convert',
      },
      {
        icon: RecordVoiceOverIcon,
        color: '#be123c',
        bg:   '#ffe4e6',
        title: 'Audio & Video Transcription',
        description: 'Transcribe interviews, jail calls, and body-cam audio into timestamped text with Whisper. Runs entirely in your browser — export as TXT, SRT, or VTT.',
        to: '/transcribe',
        tag: 'Beta',
        keywords: 'transcribe transcription whisper interview jail call body cam speech text subtitles',
      },
      {
        icon: SwapHorizIcon,
        color: '#0369a1',
        bg:   '#e0f2fe',
        title: 'Audio Format Converter',
        description: 'Convert audio or video with an unusual or legacy codec (jail calls, DVR audio) into an ordinary MP3 or WAV file that plays anywhere, with no special player needed.',
        to: '/audioconverter',
        tag: 'Beta',
        keywords: 'audio video codec mp3 wav jail call dvr convert play',
      },
    ],
  },
  {
    title: 'Maps & Field Work',
    tools: [
      {
        icon: MapIcon,
        color: '#6d28d9',
        bg:   '#f3e8ff',
        title: 'Map Search',
        description: 'Find locations where several OpenStreetMap features appear close together. Search investigative leads such as addressed buildings, building levels, entrances, cameras, transit, and critical infrastructure.',
        to: '/mapsearch',
        tag: 'Online',
        keywords: 'map openstreetmap osm cameras buildings locations search',
      },
      {
        icon: ShareLocationIcon,
        color: '#4f46e5',
        bg:   '#eef2ff',
        title: 'Video Canvass Tracker',
        description: 'Shared live map for a team pulling surveillance video: log each address, mark whether footage was obtained and if the subject was seen (and which way they went), and send the link so everyone tracks progress in real time.',
        to: '/canvass',
        tag: 'Shared · Online',
        keywords: 'video canvass cctv camera footage surveillance team map shared',
      },
      {
        icon: AltRouteIcon,
        color: '#0891b2',
        bg:   '#cffafe',
        title: 'Area Search Coordinator',
        description: 'Shared live map for a team sweeping a neighborhood for a vehicle or other item: each officer\'s driven route draws in real time so everyone can see what\'s been covered, drop pins with notes, and avoid duplicating ground.',
        to: '/areasearch',
        tag: 'Beta · Shared · Online',
        keywords: 'area search sweep gps track team map shared vehicle',
      },
    ],
  },
];

export const TOOLS = SECTIONS.flatMap(s =>
  s.subsections ? s.subsections.flatMap(sub => sub.tools) : s.tools
);

const sectionId = title => 'tools-' + title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Every word typed must appear somewhere in the tool's title, description,
// keywords, tag or section name ("apple imei" finds the 2703(d) parser).
function matchesQuery(tool, query, ...context) {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [tool.title, tool.description, tool.keywords, tool.tag, ...context].join(' ').toLowerCase();
  return words.every(w => hay.includes(w));
}

// SECTIONS narrowed to the tools that match, keeping sections and subsections
// that still have something in them.
function filterSections(query) {
  return SECTIONS.map(s => {
    if (s.subsections) {
      const subsections = s.subsections
        .map(sub => ({ ...sub, tools: sub.tools.filter(t => matchesQuery(t, query, s.title, sub.title)) }))
        .filter(sub => sub.tools.length);
      return { ...s, subsections, count: subsections.reduce((n, sub) => n + sub.tools.length, 0) };
    }
    const tools = s.tools.filter(t => matchesQuery(t, query, s.title));
    return { ...s, tools, count: tools.length };
  });
}

// Top-N most-used tools (by visit count) from Firestore, matched back to
// cards and returned in popularity order. Returns null until loaded / if
// reads are unavailable, so callers fall back to the static `favorite` flags.
function useTopTools(count = 4) {
  const [top, setTop] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'toolUsage'), orderBy('count', 'desc')));
        const cardKeys = new Set(TOOLS.map(t => keyFromPath(t.to)));
        const picks = [];
        snap.forEach(d => { if (picks.length < count && cardKeys.has(d.id)) picks.push(d.id); });
        if (!cancelled && picks.length) setTop(picks);
      } catch {
        // Reads blocked/unavailable — keep the static favorites.
      }
    })();
    return () => { cancelled = true; };
  }, [count]);
  return top;
}

// Admin-curated "Featured Tools" — driven by a single Firestore document
// (`config/featuredTools`, an ordered array of tool `to` paths) so it can be
// changed from /admin without a redeploy, same pattern as the announcement
// banner. Returns null while loading / if the read is unavailable, so the
// section just stays hidden rather than showing something wrong.
function useFeaturedTools() {
  const [paths, setPaths] = useState(null);
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'config', 'featuredTools'),
      snap => setPaths(snap.exists() ? (snap.data().toolPaths || []) : []),
      () => setPaths([]),
    );
    return unsub;
  }, []);
  return paths;
}

// `compact` sizes the card between the tiny "Top tools" chips and a full
// category card — used for the "Featured Tools" section.
function ToolCard({ tool, favorite, compact = false }) {
  const [hovered, setHovered] = useState(false);
  const Icon = tool.icon;

  const iconBubble = compact ? 34 : 44;
  const iconSize = compact ? 18 : 24;

  const card = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 8 : 12,
        background: '#fff',
        border: `1.5px solid ${hovered ? tool.color : '#e2e8f0'}`,
        borderRadius: compact ? 10 : 12,
        padding: compact ? '14px 16px' : '22px 20px',
        boxShadow: hovered
          ? `0 8px 24px rgba(0,0,0,0.10), 0 0 0 1px ${tool.color}22`
          : '0 1px 3px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'border-color .18s, box-shadow .18s, transform .15s',
        boxSizing: 'border-box',
        cursor: 'pointer',
      }}
    >
      {/* Icon bubble */}
      <div style={{
        width: iconBubble, height: iconBubble, borderRadius: compact ? 8 : 10,
        background: tool.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon style={{ fontSize: iconSize, color: tool.color }} />
      </div>

      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: compact ? 14 : 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, margin: 0 }}>
          {tool.title}
        </h3>
        {favorite && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            background: '#fffbeb', color: '#b45309',
            padding: '2px 8px', borderRadius: 20, border: '1px solid #fcd34d',
            whiteSpace: 'nowrap',
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            User Favorite
          </span>
        )}
        {tool.tag && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            background: '#fef9c3', color: '#854d0e',
            padding: '2px 7px', borderRadius: 20, border: '1px solid #fde68a',
            whiteSpace: 'nowrap',
          }}>
            {tool.tag}
          </span>
        )}
      </div>

      {/* Description */}
      <div style={{
        fontSize: compact ? 12.5 : 13, color: '#64748b', lineHeight: 1.6,
        // Compact cards cap the description at exactly 2 lines with a fixed
        // `height`, not `flex: 1` — -webkit-line-clamp on a flex-grown child
        // (basis 0%, height resolved by the flexbox algorithm) doesn't
        // reliably cut off a 3rd line in every browser; a hard pixel height
        // is the one that actually holds regardless of the flex context.
        ...(compact
          ? {
              height: 12.5 * 1.6 * 2, overflow: 'hidden',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis',
            }
          : { flex: 1 }),
      }}>
        {tool.description}
      </div>

      {/* CTA */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        fontSize: 12, fontWeight: 600,
        color: hovered ? tool.color : '#94a3b8',
        transition: 'color .18s',
        marginTop: compact ? 'auto' : 4,
      }}>
        {tool.external ? 'Open external ↗' : 'Open tool →'}
      </div>
    </div>
  );

  const linkStyle = { textDecoration: 'none', display: 'flex' };
  return tool.external
    ? <a href={tool.to} target="_blank" rel="noopener noreferrer" style={linkStyle}>{card}</a>
    : <Link to={tool.to} style={linkStyle}>{card}</Link>;
}

function Home() {
  const topTools = useTopTools(4);
  // Dynamic "User Favorite": the 4 most-used tools once usage data loads; the
  // static per-tool `favorite` flags are the fallback until then.
  const isFav = tool => (topTools ? topTools.includes(keyFromPath(tool.to)) : !!tool.favorite);
  // Same top-4 set, resolved back to full tool cards and kept in popularity
  // order, for the quick-access row under the hero.
  const quickTools = topTools
    ? topTools.map(key => TOOLS.find(t => keyFromPath(t.to) === key)).filter(Boolean)
    : TOOLS.filter(t => t.favorite);
  // Admin-curated picks (see useFeaturedTools) resolved back to full tool
  // cards, in the order set in /admin. Hidden entirely until at least one is
  // configured, so an empty list doesn't leave a section title with nothing under it.
  const featuredPaths = useFeaturedTools();
  const featuredTools = (featuredPaths || []).map(p => TOOLS.find(t => t.to === p)).filter(Boolean);

  // Search + section jump bar.
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);
  const searching = search.trim().length > 0;
  const sections = useMemo(() => filterSections(search.trim()), [search]);
  const matchCount = sections.reduce((n, s) => n + s.count, 0);
  useEffect(() => {
    // "/" jumps to the search box from anywhere on the page (unless already typing).
    const onKey = e => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const barRef = useRef(null);
  // Scroll so the section heading sits just under the sticky bar, whatever its height.
  const jumpTo = title => {
    const el = document.getElementById(sectionId(title));
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - (barRef.current?.offsetHeight || 0) - 16;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: '#0f172a',
    }}>

      {/* Hero — dark, to bridge the landing → dashboard theme */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, #0a0b16, #111827)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        {/* Glows */}
        <div aria-hidden style={{ position: 'absolute', top: -60, left: '12%', width: 280, height: 280, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,.28), transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />
        <div aria-hidden style={{ position: 'absolute', top: -10, right: '10%', width: 260, height: 260, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168,85,247,.22), transparent 65%)', filter: 'blur(20px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto', padding: '40px 40px 44px' }}>

          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
            fontSize: 13, fontWeight: 600, color: '#c7d2fe', marginBottom: 18,
          }}>
            ← Home
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
              background: 'rgba(99,102,241,.16)', color: '#c7d2fe',
              padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(99,102,241,.38)',
            }}>
              Law Enforcement
            </div>
            <span style={{ fontSize: 12, color: '#9aa3b8' }}>{TOOLS.length} tools</span>
          </div>

          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.5px', color: '#fff' }}>
            LE Tech Tools
          </h1>

          <p style={{ fontSize: 15, color: '#9aa3b8', margin: '0 0 22px', maxWidth: 600, lineHeight: 1.7 }}>
            A free collection of browser-based tools for law enforcement and investigators — LPR pattern
            analysis, Facebook, Instagram, Snapchat, Apple &amp; Google warrant-return parsers, DVR time
            and UTC converters, EXIF metadata, and more. Everything happens locally, and no information is
            ever sent to any server. Once this page loads, feel free to disable your WiFi to make sure.
          </p>

          {/* Privacy badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 16px', borderRadius: 8,
            background: 'rgba(52,211,153,.12)', border: '1px solid rgba(52,211,153,.32)',
            fontSize: 13, color: '#6ee7b7', lineHeight: 1.5,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <strong>100% local &amp; private</strong> — nothing leaves your browser
          </div>
        </div>
      </div>

      {/* Top-tools quick nav — small icon links to the current most-used tools */}
      {quickTools.length > 0 && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{
            maxWidth: 1200, margin: '0 auto', padding: '14px 40px',
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#94a3b8', flexShrink: 0 }}>
              User Favorite Tools
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {quickTools.map(tool => {
                const Icon = tool.icon;
                return (
                  <Link key={tool.to} to={tool.to} title={tool.title} style={{
                    display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999,
                    padding: '5px 12px 5px 5px',
                  }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: '50%', background: tool.bg, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon style={{ fontSize: 14, color: tool.color }} />
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{tool.title}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Search + section jump bar — stays at the top while scrolling */}
      <div ref={barRef} style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: 'rgba(248,250,252,.96)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #e2e8f0',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 40px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 560 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur(); } }}
              placeholder={`Search ${TOOLS.length} tools…`}
              title="Try a tool name or a word like IMEI, CDR, VPN or Snapchat"
              aria-label="Search tools"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 36px 9px 36px', fontSize: 14,
                border: '1.5px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#0f172a', outline: 'none',
              }}
            />
            {!searching && (
              <kbd style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 5, padding: '1px 6px', background: '#f8fafc', fontFamily: 'inherit' }}>/</kbd>
            )}
          </div>
          <nav aria-label="Tool sections" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sections.map(s => {
              const empty = searching && !s.count;
              return (
                <button key={s.title} type="button" disabled={empty} onClick={() => jumpTo(s.title)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
                  padding: '5px 11px', borderRadius: 999, border: '1px solid #e2e8f0',
                  background: empty ? 'transparent' : '#fff', color: empty ? '#cbd5e1' : '#334155',
                  cursor: empty ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}>
                  {s.title}
                  <span style={{ fontSize: 11, color: empty ? '#cbd5e1' : '#94a3b8', fontWeight: 700 }}>{s.count}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Sectioned tools */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 40px 72px' }}>
        {searching && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, fontSize: 14, color: '#475569' }}>
            {matchCount
              ? <span><strong>{matchCount}</strong> {matchCount === 1 ? 'tool matches' : 'tools match'} &ldquo;{search.trim()}&rdquo;</span>
              : <span>No tools match &ldquo;{search.trim()}&rdquo;. Try a shorter or different word.</span>}
            <button type="button" onClick={() => setSearch('')} style={{ fontSize: 13, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
              Clear search
            </button>
          </div>
        )}
        {/* Featured tools — curated from /admin (config/featuredTools) */}
        {!searching && featuredTools.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <h2 style={{
                fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                color: '#64748b', margin: 0,
              }}>
                Featured Tools
              </h2>
              <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{featuredTools.length} tool{featuredTools.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 12, alignItems: 'stretch',
            }}>
              {featuredTools.map(tool => (
                <ToolCard key={tool.to} tool={tool} favorite={isFav(tool)} compact />
              ))}
            </div>
          </div>
        )}
        {sections.filter(section => section.count).map(section => {
          const allTools = section.subsections
            ? section.subsections.flatMap(sub => sub.tools)
            : section.tools;
          return (
            <div key={section.title} id={sectionId(section.title)} style={{ marginBottom: 40 }}>
              {/* Section header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h2 style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                  color: '#64748b', margin: 0,
                }}>
                  {section.title}
                </h2>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{allTools.length} tool{allTools.length !== 1 ? 's' : ''}</span>
              </div>

              {section.subsections ? (
                /* Subsections layout */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {section.subsections.map(sub => (
                    <div key={sub.title}>
                      {/* Subsection label */}
                      <div style={{
                        fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
                        color: '#94a3b8', marginBottom: 10,
                      }}>
                        {sub.title}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                        gap: 16,
                        alignItems: 'stretch',
                      }}>
                        {sub.tools.map(tool => (
                          <ToolCard key={tool.to} tool={tool} favorite={isFav(tool)} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Flat tools grid */
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                  gap: 16,
                  alignItems: 'stretch',
                }}>
                  {section.tools.map(tool => (
                    <ToolCard key={tool.to} tool={tool} favorite={isFav(tool)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* FAQ — SEO content + matches the FAQPage structured data in index.html */}
        <section style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
              color: '#64748b', margin: 0,
            }}>
              Frequently Asked Questions
            </h2>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 16,
          }}>
            {FAQS.map(faq => (
              <div key={faq.q} style={{
                background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>{faq.q}</h3>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ background: '#fff', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 40px', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 6px', lineHeight: 1.6 }}>
            <strong style={{ color: '#0f172a' }}>LE Tech Tools</strong> — free, browser-based tools for law
            enforcement and investigators. LPR analysis, warrant-return parsers, time &amp; video converters,
            EXIF metadata, and more.
          </p>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
            100% local &amp; private — no case data is ever sent to a server.
          </p>
        </div>
      </footer>

    </div>
  );
}

const FAQS = [
  {
    q: 'Is my data private?',
    a: 'Yes. Every tool runs 100% in your browser. No uploaded file, plate, image, or case data is ever sent to a server. Once the page loads you can disable WiFi and the tools still work. Three tools are labeled exceptions, because they cannot work any other way: the Video Canvass Tracker and Area Search Coordinator share a live map with your team, and IP and Domain Intelligence looks up IP addresses and domains online — though its IP blocklist screening still runs entirely in your browser.',
  },
  {
    q: 'Are these tools free to use?',
    a: 'Yes. LE Tech Tools is free to use in any modern web browser — no account, install, or license required.',
  },
  {
    q: 'What can these tools do?',
    a: 'They help investigators analyze OpenALPR license-plate-reader data, parse Facebook, Instagram, Snapchat, Google, and Apple legal-process returns, convert DVR and UTC times, read photo EXIF metadata, generate preservation requests and subpoena riders, and coordinate a shared video canvass.',
  },
];

export default Home;
